
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[var(--btn-radius)] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-btn-selected text-btn-selected-foreground hover:bg-btn-selected/90 border border-btn-selected",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 border border-destructive/60",
        outline:
          "border border-btn-outline-border bg-background text-btn-outline-foreground hover:bg-btn-outline-hover hover:text-btn-outline-hover-foreground [border-width:var(--btn-outline-border-width)]",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border",
        ghost: "text-btn-ghost-foreground hover:bg-btn-ghost-hover hover:text-btn-ghost-hover-foreground border border-transparent hover:border-border/40",
        // THE canonical high-contrast outline (Gordon: "high contrast
        // outlines on all unselected buttons"). The same look was hand-copied
        // 16× across 5 opacity flavors before the 2026-09-10 design-system
        // audit — new code uses this variant (or `.bb-outline-contrast` on
        // non-Button elements) instead of retyping border classes. Width
        // comes from --btn-outline-border-width (one thickness for all
        // buttons — the other variants get it from the global rule in
        // globals.css, which respects a caller's border-0; Super Admin
        // default + each person's own in Settings → Layout, Gordon
        // 2026-11-01) — never hardcode border-2.
        outlineContrast:
          "border border-foreground/50 bg-transparent text-foreground hover:border-foreground hover:bg-muted/40 [border-width:var(--btn-outline-border-width)]",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
        fixedIcon: "size-[50px] flex-shrink-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
